import { create } from 'zustand';

// World zoom levels — the descent IS the LOD strategy (see architecture v0.2 §4).
export const ZOOM = {
  ORBIT: 'orbit',
  DISTRICT: 'district',
  STREET: 'street',
  INTERIOR: 'interior'
};

// Car size: tuned value baked here; ?carscale= or a saved localStorage value
// override it (in that precedence) so it can be re-tuned live in ?cars=edit.
export const CAR_SCALE_DEFAULT = 0.42;
function readCarScale() {
  try {
    const q = parseFloat(new URLSearchParams(window.location.search).get('carscale'));
    if (Number.isFinite(q) && q > 0) return q;
    const ls = parseFloat(localStorage.getItem('carscale'));
    if (Number.isFinite(ls) && ls > 0) return ls;
  } catch { /* SSR / private mode */ }
  return CAR_SCALE_DEFAULT;
}

export const useWorldStore = create((set) => ({
  zoom: ZOOM.ORBIT,
  setZoom: (zoom) => set({ zoom }),

  // Which interior we're inside (null when outdoors) — a key of ROOMS (src/rooms.js)
  interior: null, // 'studio' | 'listening-room' | 'concert-hall'
  enterInterior: (interior) => set({ interior, zoom: ZOOM.INTERIOR, spawnOverride: null, pendingEnter: null }),
  // Leaving a venue returns you to ITS storefront on the street, not a generic
  // overview — so the block feels continuous.
  exitInterior: () => set((s) => ({ interior: null, zoom: ZOOM.STREET, spawnOverride: null, streetFocus: s.interior, pendingEnter: null })),

  // Map-nav flythrough: pick a venue → fly the street camera there → once it
  // settles, auto-enter. pendingEnter holds the venue we're flying to enter.
  pendingEnter: null,
  setPendingEnter: (pendingEnter) => set({ pendingEnter }),

  // Designed spawn shipped by a room's splat manifest ({ pos, look }) — takes
  // precedence over the ROOMS default once the splat loads.
  spawnOverride: null,
  setSpawnOverride: (spawnOverride) => set({ spawnOverride }),

  // A world-space landmark reported by the loaded city model (e.g. the parked
  // car) — the model fly-in lands beside it. [x,y,z] or null.
  landmark: null,
  setLandmark: (landmark) => set({ landmark }),

  // Venue anchors inside the loaded city model: { <venueKey>: [x,y,z] } — the
  // real building each venue sits on, computed after the model loads.
  venueAnchors: null,
  setVenueAnchors: (venueAnchors) => set({ venueAnchors }),

  // Bounding box of each venue's building: { <venueKey>: { center:[x,y,z],
  // size:[w,h,d] } } — used to make the whole building clickable + hover-glow.
  venueBoxes: null,
  setVenueBoxes: (venueBoxes) => set({ venueBoxes }),

  // A one-shot walk teleport target ({ pos:[x,y,z], look:[x,y,z] }) — quick
  // travel while walking. WalkControls consumes it and clears it.
  walkTarget: null,
  setWalkTarget: (walkTarget) => set({ walkTarget }),

  // Debug mesh picker (?pick=1): the last mesh clicked in the city model, so we
  // can identify a specific sign to swap dynamic graphics onto.
  picked: null,
  setPicked: (picked) => set({ picked }),

  // Which town location the STREET camera is framing (a TOWN_LOCATIONS key, or
  // null for the establishing block overview). The HUD minimap sets this to fly
  // the camera to a venue; entering a location goes through enterInterior.
  streetFocus: null,
  setStreetFocus: (streetFocus) => set({ streetFocus, zoom: ZOOM.STREET, interior: null }),

  // Walkable bounds of the current splat room ({min:[x,y,z], max:[x,y,z]}),
  // measured from the splat's own extents on load. The camera is clamped
  // inside them so visitors never see the room's fuzzy outer shell.
  roomBounds: null,
  setRoomBounds: (roomBounds) => set({ roomBounds }),
  boundsEnabled: true,
  setBoundsEnabled: (boundsEnabled) => set({ boundsEnabled }),

  // Bumped when the camera panel saves/resets a room's tuned config so the
  // scene re-reads the localStorage override.
  camVersion: 0,
  bumpCamVersion: () => set((s) => ({ camVersion: s.camVersion + 1 })),

  // False while the camera rig is flying between zoom levels. Orbit
  // constraints only engage once settled — clamping a mid-flight camera
  // yanks it off the path and entry lands somewhere random.
  camSettled: false,
  setCamSettled: (camSettled) => set({ camSettled }),

  // Live Anam video-chat stream (an HTMLVideoElement carrying the WebRTC
  // feed) — when set, the room's VideoPanel swaps its recorded cameo for
  // this live texture. Owned by LiveChatDock (DOM side).
  liveVideoEl: null,
  setLiveVideoEl: (liveVideoEl) => set({ liveVideoEl }),

  // Voice chat with a 3D resident (e.g. Rebel on the street). chatAvatar holds
  // { avatarId, name } while a conversation is open; AvatarChatDock (DOM side)
  // drives the push-to-talk turn loop. Clicking the character opens it.
  chatAvatar: null,
  openChat: (chatAvatar) => set({ chatAvatar }),
  closeChat: () => set({ chatAvatar: null }),
  // True while that resident is actively speaking — AnimatedResident crossfades
  // walk→idle and layers a talking head-gesture (per-frame amplitude arrives via
  // talkBridge, not the store, to avoid re-rendering the scene each frame).
  residentTalking: false,
  setResidentTalking: (residentTalking) => set({ residentTalking }),

  // Broadcast layer (the district's live station) vs pulse layer (ambient soundscape)
  musicOn: true,
  toggleMusic: () => set((s) => ({ musicOn: !s.musicOn })),

  // Live now-playing state from the district's station [reuse /api/radio/stations/:slug/now]
  nowPlaying: null,
  setNowPlaying: (nowPlaying) => set({ nowPlaying }),

  // Compact per-track modulation track: beat + sentiment driving the world's mood
  // (architecture v0.2 §5). Derived from existing track analysis.
  modulation: null,
  setModulation: (modulation) => set({ modulation }),

  // Venue presence: other listeners as felt figures [reuse listening-room presence]
  presences: [], // [{ userId, handle }]
  setPresences: (presences) => set({ presences }),

  // Routes editor (?routes=edit) selection — lifted so the on-screen RoutesPanel,
  // the clickable 3D node handles, and the keyboard all target the same node.
  routeEdit: { routeIdx: 0, ptIdx: 0 },
  setRouteEdit: (patch) => set((s) => ({ routeEdit: { ...s.routeEdit, ...patch } })),

  // NPC placement editor (?place=edit): the loaded roster (for the panel list)
  // and which NPC is selected for moving. Position overrides persist per-slug in
  // localStorage (npcpos:<slug>); copy the logged post line back into roster.json.
  npcList: [],
  setNpcList: (npcList) => set({ npcList }),
  npcEdit: { slug: null },
  setNpcEdit: (patch) => set((s) => ({ npcEdit: { ...s.npcEdit, ...patch } })),

  // Which admin tool is active (one at a time): null | 'cars' | 'guard' |
  // 'performer' | 'place' | 'routes' | 'cam' | 'bb'. The single AdminPanel hub
  // drives this; every tuning panel / in-scene edit mode reads it. Seeded from
  // the legacy ?<key>=edit params so old deep-links still open the right tool.
  adminTool: (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get('cars') === 'edit') return 'cars';
      if (p.get('guard') === 'edit') return 'guard';
      if (p.get('place') === 'edit') return 'place';
      if (p.get('routes') === 'edit') return 'routes';
      if (p.get('cam') === 'edit') return 'cam';
      if (p.get('bb') === 'edit') return 'bb';
    } catch { /* SSR */ }
    return null;
  })(),
  setAdminTool: (adminTool) => set((s) => ({ adminTool: s.adminTool === adminTool ? null : adminTool })),

  // Avatar ids this viewer follows / owns — the map pins the ones also present
  // as residents (with a headshot marker + fast-travel). Owned = "mine," shown
  // distinct from followed.
  following: [],
  setFollowing: (following) => set({ following }),
  owned: [],
  setOwned: (owned) => set({ owned }),

  // Show/hide the other-people markers (owned + followed avatar headshot pins) on
  // the HUD minimap. Owning a lot of avatars clouds the map, so this is a toggle
  // (persisted). Off = only the venue dots show.
  showPeople: (() => { try { return localStorage.getItem('showPeople') !== '0'; } catch { return true; } })(),
  toggleShowPeople: () => set((s) => {
    const showPeople = !s.showPeople;
    try { localStorage.setItem('showPeople', showPeople ? '1' : '0'); } catch { /* private mode */ }
    return { showPeople };
  }),

  // Concert-hall performer tuning (height + feet), live-adjustable in-scene via
  // PerformerPanel. Video and still frame differently (video = landscape with
  // margins), so seed per-source; ?perfh/?perfy still override, then the panel drives it.
  perfTune: (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const q = (k, d) => { const v = parseFloat(p.get(k)); return Number.isFinite(v) ? v : d; };
      const isVideo = !!p.get('perfvideo');
      return { h: q('perfh', isVideo ? 2.75 : 2.65), y: q('perfy', isVideo ? 1.98 : 1.74) };
    } catch { return { h: 2.65, y: 1.74 }; }
  })(),
  setPerfTune: (patch) => set((s) => ({ perfTune: { ...s.perfTune, ...patch } })),

  // Concert-hall security guard placement (offset from the concert-hall building
  // anchor), live-adjustable via GuardPanel (?guard=edit). Seeds from ?guard*.
  guardTune: (() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const q = (k, d) => { const v = parseFloat(p.get(k)); return Number.isFinite(v) ? v : d; };
      return { x: q('guardx', -0.75), y: q('guardy', 0.1), z: q('guardz', 3.5), rot: q('guardrot', 0), h: q('guardh', 0.68) };
    } catch { return { x: -0.75, y: 0.1, z: 3.5, rot: 0, h: 0.68 }; }
  })(),
  setGuardTune: (patch) => set((s) => ({ guardTune: { ...s.guardTune, ...patch } })),

  // Radio DJ video-host cameo on the big billboards (dynamic moments only —
  // scheduled narrations stay audio-only, see useDjBillboard). phase mirrors the
  // website cameo machine; videoEl is the hidden <video> the Anam stream binds
  // to, which CityBillboards samples into a VideoTexture while live.
  djCameo: { phase: 'idle', hostName: null, videoEl: null },
  setDjCameo: (patch) => set((s) => ({ djCameo: { ...s.djCameo, ...patch } })),

  // Car size knob (?cars=edit). The cars (hero Lowrider + route traffic) were
  // authored at real-world meters, but the town is placed at a reduced scale to
  // match the small avatars — so cars need scaling down. One global scalar,
  // live-tunable and persisted to localStorage; bake the tuned value as CAR_SCALE_DEFAULT.
  carScale: readCarScale(),
  setCarScale: (v) => set(() => {
    const carScale = Math.max(0.05, Math.round(v * 1000) / 1000);
    try { localStorage.setItem('carscale', String(carScale)); } catch { /* private mode */ }
    return { carScale };
  })
}));